import type { ReactNode } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';

export type RequireParamsProps = {
  /** Parameters the wrapped page cannot work without. */
  params: string[];
  /**
   * Local storage keys to fall back to, by parameter name, so a patient who
   * refreshes or loses the query string is not turned away. A parameter with
   * no entry here has to be in the URL.
   */
  fallbackKeys?: Record<string, string>;
  /** Where to send a patient whose parameters are missing. */
  redirectTo?: string;
  children: ReactNode;
};

/**
 * Sends a patient away unless every named parameter is present.
 *
 * The forms in the pre-screening flow carry the patient's details between
 * pages, so a page reached without them would collect answers that cannot be
 * attached to anybody.
 */
export const RequireParams = ({ params, fallbackKeys = {}, redirectTo = '/error', children }: RequireParamsProps) => {
  // The router's location rather than `window.location`, so a parameter added
  // by a redirect within the app counts.
  const [searchParams] = useSearchParams();

  const missing = params.filter((param) => {
    if (searchParams.get(param)) {
      return false;
    }

    const fallbackKey = fallbackKeys[param];

    if (!fallbackKey) {
      return true;
    }

    try {
      return !localStorage.getItem(fallbackKey);
    } catch {
      // Blocked storage is not a reason to turn a patient away.
      return true;
    }
  });

  if (missing.length > 0) {
    return <Navigate to={redirectTo} />;
  }

  return children;
};

export default RequireParams;
